import { useCallback, useEffect, useRef, useState } from 'react';
import { ResearchSummary } from '../types/bom.types';
import { isResearchRunning } from '../components/discovery/researchProgress';

/**
 * The research summary popup opens when the buyer arrives on discovery while
 * research is still running — every arrival, so coming back from the
 * dashboard mid-research shows the same popup (with the server's live ETA)
 * as getting here from validation. Once closed, the progress strip carries
 * the wait for the rest of this visit; status polls never reopen it.
 */
export const useResearchArrival = (bomId: string | undefined, summary: ResearchSummary | null) => {
  const [open, setOpen] = useState(false);
  const running = isResearchRunning(summary);
  // The BOM this visit has already shown the popup for.
  const shownFor = useRef<string | null>(null);

  useEffect(() => {
    if (!bomId || !running || shownFor.current === bomId) return;
    shownFor.current = bomId;
    setOpen(true);
  }, [bomId, running]);

  // A finished wait dissolves: the popup goes as soon as the last part lands.
  useEffect(() => {
    if (!running) setOpen(false);
  }, [running]);

  const close = useCallback(() => setOpen(false), []);
  const reopen = useCallback(() => setOpen(true), []);
  return { open, close, reopen };
};

export default useResearchArrival;
